import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getUpcomingDeadlines, getOverdueDeadlines } from '@/services/deadlineService';
import type { Deadline } from '@/types/deadlines';
import { differenceInDays, parseISO, isToday } from 'date-fns';

export interface DeadlineNotification {
  id: string;
  deadline: Deadline;
  type: 'expired' | 'today' | 'urgent' | 'soon' | 'upcoming';
  message: string;
  daysRemaining: number;
  read: boolean;
  createdAt: Date;
}

function getNotificationType(daysRemaining: number): DeadlineNotification['type'] {
  if (daysRemaining < 0) return 'expired';
  if (daysRemaining === 0) return 'today';
  if (daysRemaining <= 3) return 'urgent';
  if (daysRemaining <= 7) return 'soon';
  return 'upcoming';
}

function getNotificationMessage(deadline: Deadline, daysRemaining: number): string {
  if (daysRemaining < 0) {
    const overdueDays = Math.abs(daysRemaining);
    return `Vencido hace ${overdueDays} día${overdueDays > 1 ? 's' : ''}`;
  }
  if (daysRemaining === 0) return 'Vence hoy';
  if (daysRemaining === 1) return 'Vence mañana';
  return `Vence en ${daysRemaining} días`;
}

export function useDeadlineNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<DeadlineNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [readNotifications, setReadNotifications] = useState<Set<string>>(new Set());

  // Load read notifications from localStorage
  useEffect(() => {
    if (user) {
      const stored = localStorage.getItem(`deadline-notifications-read-${user.id}`);
      if (stored) {
        try {
          setReadNotifications(new Set(JSON.parse(stored)));
        } catch {
          setReadNotifications(new Set());
        }
      }
    }
  }, [user]);

  const loadNotifications = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const [upcoming, overdue] = await Promise.all([
        getUpcomingDeadlines(14), // Next 14 days
        getOverdueDeadlines(),
      ]);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const allDeadlines = [...overdue, ...upcoming];
      const uniqueDeadlines = allDeadlines.filter(
        (d, i, arr) => arr.findIndex(x => x.id === d.id) === i
      );

      const notificationsList: DeadlineNotification[] = uniqueDeadlines.map(deadline => {
        const dueDate = parseISO(deadline.due_date);
        const daysRemaining = differenceInDays(dueDate, today);
        
        return {
          id: `notif-${deadline.id}`,
          deadline,
          type: getNotificationType(daysRemaining),
          message: getNotificationMessage(deadline, daysRemaining),
          daysRemaining,
          read: readNotifications.has(deadline.id),
          createdAt: new Date(),
        };
      });

      // Sort: expired first, then by days remaining
      notificationsList.sort((a, b) => {
        if (a.type === 'expired' && b.type !== 'expired') return -1;
        if (a.type !== 'expired' && b.type === 'expired') return 1;
        return a.daysRemaining - b.daysRemaining;
      });

      setNotifications(notificationsList);
    } catch (error) {
      console.error('Error loading deadline notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, readNotifications]);

  useEffect(() => {
    loadNotifications();
    
    // Refresh every 30 minutes (deadlines don't change frequently)
    const interval = setInterval(loadNotifications, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const markAsRead = useCallback((deadlineId: string) => {
    if (!user) return;
    
    setReadNotifications(prev => {
      const newSet = new Set(prev);
      newSet.add(deadlineId);
      localStorage.setItem(
        `deadline-notifications-read-${user.id}`,
        JSON.stringify([...newSet])
      );
      return newSet;
    });

    setNotifications(prev =>
      prev.map(n =>
        n.deadline.id === deadlineId ? { ...n, read: true } : n
      )
    );
  }, [user]);

  const markAllAsRead = useCallback(() => {
    if (!user) return;
    
    const allIds = notifications.map(n => n.deadline.id);
    setReadNotifications(prev => {
      const newSet = new Set([...prev, ...allIds]);
      localStorage.setItem(
        `deadline-notifications-read-${user.id}`,
        JSON.stringify([...newSet])
      );
      return newSet;
    });

    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [user, notifications]);

  const clearReadStatus = useCallback(() => {
    if (!user) return;
    localStorage.removeItem(`deadline-notifications-read-${user.id}`);
    setReadNotifications(new Set());
    setNotifications(prev => prev.map(n => ({ ...n, read: false })));
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const urgentCount = notifications.filter(
    n => !n.read && (n.type === 'expired' || n.type === 'today' || n.type === 'urgent')
  ).length;

  return {
    notifications,
    isLoading,
    unreadCount,
    urgentCount,
    markAsRead,
    markAllAsRead,
    clearReadStatus,
    refresh: loadNotifications,
  };
}
